import { useEffect, useState, ReactNode, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useMFA } from '@/hooks/useMFA';
import { Loader2 } from 'lucide-react';
import { useLocalizedNavigate } from '@/components/i18n';
import { logger } from '@/lib/logger';
import { trackEvent } from '@/lib/posthog';
import { supabase } from '@/lib/supabase/client';

interface ProtectedRouteProps {
  children: ReactNode;
  requireOnboarding?: boolean;
  requireMFA?: boolean;
}

export function ProtectedRoute({
  children,
  requireOnboarding = false,
  requireMFA = false,
}: ProtectedRouteProps) {
  const { user, profile, loading, refreshProfile, signOut } = useAuth();
  const location = useLocation();
  const pathname = location.pathname;
  const navigate = useLocalizedNavigate();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { hasEnrolledMFA, isAtAAL2, isLoading: mfaLoading } = useMFA();
  const [mfaCheckStable, setMfaCheckStable] = useState(false);
  const hasLoggedRender = useRef(false);
  const [isSigningOutForMFA, setIsSigningOutForMFA] = useState(false);

  // Debug logging (once per significant state change)
  useEffect(() => {
    if (!hasLoggedRender.current || !loading) {
      logger.log('[ProtectedRoute] State:', {
        path: pathname,
        userId: user?.id?.substring(0, 8) ?? 'null',
        isAdvisor: profile?.is_advisor,
        loading,
        mfaCheckStable,
      });
      hasLoggedRender.current = true;
    }
  }, [pathname, user?.id, profile?.is_advisor, loading, mfaCheckStable]);

  // Stabilize MFA check - only run once when loading completes
  const mfaStabilizedRef = useRef(false);
  useEffect(() => {
    if (!mfaLoading && !mfaStabilizedRef.current) {
      logger.log('[ProtectedRoute] MFA check completed, stabilizing. hasEnrolledMFA:', hasEnrolledMFA, 'isAtAAL2:', isAtAAL2);
      mfaStabilizedRef.current = true;
      setMfaCheckStable(true);
    }
  }, [mfaLoading, hasEnrolledMFA, isAtAAL2]);

  // CRITICAL SECURITY CHECK: Detect MFA bypass attempts
  // If user has enrolled MFA but hasn't verified in this session (not at AAL2),
  // they may have navigated away from the MFA challenge (back button, direct URL, etc.)
  // We must sign them out and force re-authentication with MFA
  const hasTriggeredMFAEnforcement = useRef(false);
  useEffect(() => {
    async function enforceMFA() {
      // Only run this check once per session when conditions are met
      if (hasTriggeredMFAEnforcement.current) return;

      // Wait for MFA status to stabilize and require MFA for this route
      if (!requireMFA || !mfaCheckStable || mfaLoading) return;

      // Check: User has enrolled MFA (has verified factors) but current session is NOT at AAL2
      // This means they logged in but didn't complete MFA verification
      if (hasEnrolledMFA && !isAtAAL2) {
        hasTriggeredMFAEnforcement.current = true;
        logger.log('[ProtectedRoute] SECURITY: MFA bypass detected! User has enrolled MFA but session is at AAL1. Signing out.');

        // Sign out to clear the AAL1 session and force full re-auth with MFA
        setIsSigningOutForMFA(true);
        await signOut();

        // Navigate to auth with a message indicating MFA is required
        navigate('/auth?mfa_required=true', { replace: true });
      }
    }

    if (user && mfaCheckStable) {
      enforceMFA();
    }
  }, [user, requireMFA, mfaCheckStable, mfaLoading, hasEnrolledMFA, isAtAAL2, signOut, navigate]);

  // If we need onboarding but profile seems stale, try refreshing once
  const hasAttemptedProfileRefresh = useRef(false);
  useEffect(() => {
    if (requireOnboarding && user && profile && !profile.onboarding_complete && !isRefreshing && !hasAttemptedProfileRefresh.current) {
      hasAttemptedProfileRefresh.current = true;
      const timer = setTimeout(() => {
        if (profile && !profile.onboarding_complete) {
          logger.log('[ProtectedRoute] Profile appears stale, refreshing once...');
          setIsRefreshing(true);
          refreshProfile().finally(() => setIsRefreshing(false));
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [requireOnboarding, user, profile, refreshProfile, isRefreshing]);

  // Update last_active_at for retention tracking (throttled to once per 5 minutes)
  const lastActiveUpdateRef = useRef<number>(0);
  useEffect(() => {
    if (!user) return;

    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000;

    // Only update if more than 5 minutes since last update
    if (now - lastActiveUpdateRef.current > fiveMinutes) {
      lastActiveUpdateRef.current = now;
      supabase
        .from('profiles')
        .update({ last_active_at: new Date().toISOString() })
        .eq('id', user.id)
        .then(({ error }) => {
          if (error) {
            console.warn('[ProtectedRoute] Failed to update last_active_at:', error.message);
          }
        }, (err: unknown) => {
          // The query builder is a PromiseLike (no .catch); the rejection handler goes on then().
          console.warn('[ProtectedRoute] Error updating last_active_at:', err instanceof Error ? err.message : 'Unknown error');
        });
    }
  }, [user, pathname]); // Update on user change or navigation

  // Memoize loading state to prevent flicker
  const isStillLoading = useMemo(
    () => loading || isRefreshing || isSigningOutForMFA || (requireMFA && !mfaCheckStable),
    [loading, isRefreshing, isSigningOutForMFA, requireMFA, mfaCheckStable]
  );

  // Memoize waiting for profile state
  const isWaitingForProfile = useMemo(
    () => requireOnboarding && !profile && user && !loading,
    [requireOnboarding, profile, user, loading]
  );

  // Memoize redirect target to prevent recalculation
  const redirectTarget = useMemo((): string | null => {
    // Still loading - no redirect yet
    if (isStillLoading) {
      return null;
    }

    // If we're signing out for MFA enforcement, don't redirect yet (effect handles it)
    if (isSigningOutForMFA) {
      return null;
    }

    // No user - redirect to auth, remembering where they were going (the extension's consent page, a deep link).
    if (!user) {
      const here = `${pathname}${typeof window !== 'undefined' ? window.location.search : ''}`;
      return here && here !== '/' ? `/auth?returnTo=${encodeURIComponent(here)}` : '/auth';
    }

    // MFA enforcement checks (when requireMFA is true)
    // Note: MFA is optional — users are NOT redirected to enroll.
    // However, if a user HAS enrolled MFA, they must verify (AAL2) to access protected routes.
    if (requireMFA && mfaCheckStable) {
      // User HAS enrolled MFA but NOT at AAL2 - this is handled by the enforceMFA effect
      // which will sign them out. Return null here to show loading while that happens.
      if (hasEnrolledMFA && !isAtAAL2) {
        logger.log('[ProtectedRoute] User has MFA but not at AAL2, waiting for enforcement...');
        return null;
      }
    }

    // Advisors accessing client-only routes get redirected — but NOT
    // dual-account users (admin + advisor) who also have their own case.
    const isDualAccount = profile?.account_type === 'dual' || profile?.role === 'admin';
    if (profile?.is_advisor && requireOnboarding && !isDualAccount) {
      logger.log('[ProtectedRoute] Phase 5: Advisor detected on client route, redirecting to /advisor', {
        userId: profile.id?.substring(0, 8),
        requestedPath: pathname,
      });
      return '/advisor';
    }

    // Onboarding not complete
    if (requireOnboarding && profile && !profile.onboarding_complete) {
      return '/onboarding';
    }

    // Waiting for profile (no redirect, just wait)
    if (requireOnboarding && !profile) {
      return null;
    }

    // No redirect needed
    return null;
  }, [isStillLoading, isSigningOutForMFA, user, requireMFA, mfaCheckStable, hasEnrolledMFA, isAtAAL2, requireOnboarding, profile]);

  // Handle navigation in useEffect (not during render)
  useEffect(() => {
    if (redirectTarget && !isStillLoading) {
      logger.log('[ProtectedRoute] Navigating to:', redirectTarget);

      // Phase 5: Track routing decisions in PostHog for monitoring
      const reason = redirectTarget === '/advisor' ? 'advisor_on_client_route'
        : redirectTarget.startsWith('/auth') ? 'unauthenticated'
        : redirectTarget === '/onboarding' ? 'onboarding_incomplete'
        : 'other';

      trackEvent('protected_route_redirect', {
        from: pathname,
        to: redirectTarget,
        reason,
        is_advisor: profile?.is_advisor ?? false,
        has_profile: !!profile,
      });

      navigate(redirectTarget, { replace: true });
    }
  }, [redirectTarget, isStillLoading, navigate, pathname, profile]);

  // Show loading spinner while checking auth/MFA or waiting for redirect
  if (isStillLoading || redirectTarget || isWaitingForProfile) {
    return (
      <div className="min-h-dvh flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // All checks passed
  logger.log('[ProtectedRoute] All checks passed, rendering children for path:', pathname);
  return <>{children}</>;
}

export default ProtectedRoute;
